import { ThemeEvents, DiscountUpdateEvent } from "./events.js";

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.appliedDiscounts = new Set();
    const debug = false;
  }

  log(msg) {
    if (debug) {
      console.log("🛒 [CartDiscount]:", msg);
    }
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.open = this.querySelector(".insert-code");
    this.error = this.querySelector(".discount-error");
    this.discountsList = this.querySelector(".applied-discounts-list");

    if (this.form) {
      this.form.addEventListener("submit", this.apply);
    }

    if (this.open) {
      this.open.addEventListener("click", this.toggle);
    }

    this.removeBtn = this.querySelector(".remove-discount");
    if (this.removeBtn) {
      this.removeBtn.addEventListener("click", this.clearAllDiscounts);
    }

    // Garantir que o campo input sempre venha limpo
    this.clearDiscountInput();

    // Carregar descontos salvos do localStorage
    this.loadSavedDiscounts();
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener("submit", this.apply);
    if (this.open) this.open.removeEventListener("click", this.toggle);
    this.teardownRefazer();
  }

  loadSavedDiscounts() {
    // Primeiro, carregar do localStorage
    const saved = localStorage.getItem("appliedDiscounts");
    if (saved) {
      try {
        const discounts = JSON.parse(saved);
        this.appliedDiscounts = new Set(discounts);
      } catch (e) {
        console.error("Erro ao carregar descontos salvos:", e);
      }
    }

    // Em seguida, sincronizar com o carrinho atual
    this.syncWithCartDiscounts();
    this.renderDiscountsList();
  }

  async syncWithCartDiscounts() {
    try {
      // Buscar dados atuais do carrinho
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const cartData = await response.json();

        // Verificar se há discount_codes aplicados
        if (cartData.discount_codes && Array.isArray(cartData.discount_codes)) {
          const currentDiscounts = new Set();

          // Adicionar apenas códigos que estão realmente aplicados (applicable: true)
          cartData.discount_codes.forEach((discount) => {
            if (discount.applicable) {
              currentDiscounts.add(discount.code);
            }
          });

          // Atualizar nosso estado local
          this.appliedDiscounts = currentDiscounts;
          this.saveDiscounts();

        //   this.log("Descontos sincronizados com carrinho:", [
        //     ...currentDiscounts,
        //   ]);
        }
      }
    } catch (error) {
      console.error("Erro ao sincronizar descontos com carrinho:", error);
    }
  }

  saveDiscounts() {
    localStorage.setItem(
      "appliedDiscounts",
      JSON.stringify([...this.appliedDiscounts])
    );
  }

  clearDiscountInput() {
    const discountInput = this.form?.querySelector('[name="discount"]');
    if (discountInput) {
      discountInput.value = "";
    }
  }

  toggle = (e) => {
    e.preventDefault();
    const isOpen = this.dataset.open === "opened";
    this.dataset.open = isOpen ? "closed" : "opened";
    this.dispatchEvent(
      new CustomEvent(ThemeEvents.DISCOUNT_TOGGLE, {
        detail: { open: !isOpen, sectionId: this.dataset.sectionId },
      })
    );

    // Sempre limpar o campo input, independente do estado
    this.clearDiscountInput();
    this.error.hidden = true;

    this.open.textContent = isOpen ? "Inserir código" : "Fechar";
  };

  apply = async (e) => {
    e.preventDefault();
    const discountCode = this.form
      .querySelector('[name="discount"]')
      .value.trim();

    if (!discountCode) {
      this.error.textContent = "Por favor, insira um código de desconto.";
      this.error.hidden = false;
      return;
    }

    if (this.appliedDiscounts.has(discountCode)) {
      this.error.textContent = "Este código de desconto já foi aplicado.";
      this.error.hidden = false;
      return;
    }

    // Adicionar estado de loading
    const submitButton =
      this.form.querySelector('button[type="submit"]') ||
      this.form.querySelector('input[type="submit"]');
    const originalButtonContent =
      submitButton?.innerHTML || submitButton?.value;

    if (submitButton) {
      submitButton.disabled = true;
      if (submitButton.innerHTML !== undefined) {
        submitButton.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" style="margin-right: 8px;">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
          Aplicando...
        `;
      } else {
        submitButton.value = "Aplicando...";
      }
    }

    try {
      // Para múltiplos descontos, enviar todos os códigos como string separada por vírgula
      const allDiscounts = [...this.appliedDiscounts, discountCode];
      const discountString = allDiscounts.join(",");

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          discount: discountString,
        }),
      });

      const data = await response.json();

      if (data.status && data.status !== 200) {
        this.error.textContent = data.description;
        this.error.hidden = false;
        return;
      }

      // Adicionar o desconto à lista e salvar
      this.appliedDiscounts.add(discountCode);
      this.saveDiscounts();

      this.error.hidden = true;
      this.clearDiscountInput();

      // Sincronizar com dados do carrinho e renderizar
      await this.syncWithCartDiscounts();
      this.renderDiscountsList();

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error(error);
      this.error.textContent = "Erro ao aplicar desconto. Tente novamente.";
      this.error.hidden = false;
    } finally {
      // Restaurar botão
      if (submitButton) {
        submitButton.disabled = false;
        if (submitButton.innerHTML !== undefined) {
          submitButton.innerHTML = originalButtonContent || "Aplicar";
        } else {
          submitButton.value = originalButtonContent || "Aplicar";
        }
      }
    }
  };

  async renderDiscountsList() {
    if (!this.discountsList) return;

    if (this.appliedDiscounts.size === 0) {
      this.discountsList.innerHTML = "";
      this.discountsList.hidden = true;
      return;
    }

    // Buscar dados atuais do carrinho para verificar quais descontos são aplicáveis
    let cartData = null;
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      if (response.ok) {
        cartData = await response.json();
      }
    } catch (error) {
      console.error("Erro ao buscar dados do carrinho:", error);
    }

    const discountsArray = [...this.appliedDiscounts];

    // Layout horizontal similar ao exemplo mostrado
    this.discountsList.innerHTML = `
      <div class="applied-discounts-container">
        <div class="discount-tags">
          ${discountsArray
            .map((code) => {
              // Verificar se o desconto é aplicável
              let isApplicable = true;
              let discountInfo = null;

              if (cartData && cartData.discount_codes) {
                discountInfo = cartData.discount_codes.find(
                  (d) => d.code === code
                );
                isApplicable = discountInfo ? discountInfo.applicable : false;
              }

              return `
              <div class="discount-tag ${
                !isApplicable ? "discount-tag--not-applicable" : ""
              }">
                <span class="icon-discount-tag">
                  <svg width="16" height="16" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path stroke="#50AA6C" fill-rule="evenodd" clip-rule="evenodd" d="M0.6875 2.25C0.6875 1.11091 1.61091 0.1875 2.75 0.1875H13.25C14.3891 0.1875 15.3125 1.11091 15.3125 2.25V4.5C15.3125 4.81066 15.0607 5.0625 14.75 5.0625C14.5092 5.0625 14.2703 5.15392 14.0871 5.33709C13.721 5.7032 13.721 6.2968 14.0871 6.66291C14.2703 6.84608 14.5092 6.9375 14.75 6.9375C15.0607 6.9375 15.3125 7.18934 15.3125 7.5V9.75001C15.3125 10.8891 14.3891 11.8125 13.25 11.8125H2.75C1.61091 11.8125 0.6875 10.8891 0.6875 9.75V7.5C0.6875 7.18934 0.93934 6.9375 1.25 6.9375C1.4908 6.9375 1.72974 6.84608 1.91291 6.66291C2.27903 6.2968 2.27903 5.7032 1.91291 5.33709C1.72974 5.15392 1.4908 5.0625 1.25 5.0625C0.93934 5.0625 0.6875 4.81066 0.6875 4.5V2.25ZM2.75 1.3125C2.23223 1.3125 1.8125 1.73223 1.8125 2.25V4.01543C2.1405 4.10814 2.45037 4.28356 2.70841 4.54159C3.51386 5.34705 3.51386 6.65295 2.70841 7.45841C2.45037 7.71644 2.1405 7.89186 1.8125 7.98457V9.75C1.8125 10.2678 2.23223 10.6875 2.75 10.6875H5.9375V1.3125H2.75ZM7.0625 1.3125V10.6875H13.25C13.7678 10.6875 14.1875 10.2678 14.1875 9.75001V7.98457C13.8595 7.89186 13.5496 7.71644 13.2916 7.45841C12.4861 6.65295 12.4861 5.34705 13.2916 4.54159C13.5496 4.28356 13.8595 4.10814 14.1875 4.01543V2.25C14.1875 1.73223 13.7678 1.3125 13.25 1.3125H7.0625Z" fill="#292929"></path>
                  </svg>
                </span>
                <span class="discount-code">${code}</span>
                ${
                  !isApplicable
                    ? '<span class="discount-status">Não aplicável</span>'
                    : ""
                }
                <button type="button" class="remove-single-discount" data-discount="${code}" aria-label="Remover ${code}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#7045F5"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#7045F5"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#7045F5"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#7045F5"></path>
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#7045F5"></path>
                  </svg>
                </button>
              </div>
            `;
            })
            .join("")}
        </div>
        <div class="discount-actions">
          <div class="discount-info">
            <span>Cupons aplicados (${discountsArray.length})</span>
          </div>
          <button type="button" class="remove-all-discounts">
            <span>Remover todos</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#00a0b1"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#00a0b1"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#00a0b1"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#00a0b1"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#00a0b1"></path>
            </svg>
          </button>
        </div>
      </div>
    `;

    this.discountsList.hidden = false;

    // Adicionar event listeners para os botões de remoção individual
    this.discountsList
      .querySelectorAll(".remove-single-discount")
      .forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Buscar o código do desconto tanto no botão quanto nos pais
          const button = e.target.closest(".remove-single-discount");
          const discountToRemove = button?.dataset?.discount;

          if (discountToRemove) {
            this.removeSingleDiscount(discountToRemove, button);
          }
        });
      });

    // Event listener para remover todos os cupons
    const removeAllBtn = this.discountsList.querySelector(
      ".remove-all-discounts"
    );
    if (removeAllBtn) {
      removeAllBtn.addEventListener("click", (e) => {
        this.clearAllDiscounts(e.target.closest(".remove-all-discounts"));
      });
    }
  }

  removeSingleDiscount = async (discountCode, buttonElement = null) => {
    try {
      // Adicionar estado de loading ao botão
      if (buttonElement) {
        buttonElement.disabled = true;
        buttonElement.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        `;
      }

      // Remover da lista local
      this.appliedDiscounts.delete(discountCode);

      // Estratégia: limpar todos os cupons e reaplicar os restantes
      const remainingDiscounts = [...this.appliedDiscounts];

      // Primeiro, limpar todos os cupons
      await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: "" }),
      });

      // Se houver cupons restantes, reaplica-los um por um com delay
      if (remainingDiscounts.length > 0) {
        for (let i = 0; i < remainingDiscounts.length; i++) {
          const code = remainingDiscounts[i];
          await fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ discount: code }),
          });

          // Pequeno delay entre as aplicações para garantir processamento
          if (i < remainingDiscounts.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        }
      }

      // Buscar dados atualizados do carrinho
      const finalResponse = await fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      const data = await finalResponse.json();

      this.saveDiscounts();
      await this.syncWithCartDiscounts();
      this.renderDiscountsList();

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error("Erro ao remover desconto:", error);

      // Restaurar botão em caso de erro
      if (buttonElement) {
        buttonElement.disabled = false;
        buttonElement.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#7045F5"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#7045F5"></path>
          </svg>
        `;
      }
    }
  };

  clearAllDiscounts = async (buttonElement = null) => {
    try {
      // Adicionar estado de loading ao botão
      if (buttonElement) {
        const originalContent = buttonElement.innerHTML;
        buttonElement.disabled = true;
        buttonElement.innerHTML = `
          <span>Removendo...</span>
          <svg width="16" height="16" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="2" fill="none" stroke-dasharray="32" stroke-dashoffset="32">
              <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        `;
      }

      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: "" }),
      });

      const data = await response.json();

      this.appliedDiscounts.clear();
      this.saveDiscounts();
      this.renderDiscountsList();

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.dataset.sectionId)
      );

      // Recarregar página se estiver na URL /cart
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error(error);

      // Restaurar botão em caso de erro
      if (buttonElement) {
        buttonElement.disabled = false;
        buttonElement.innerHTML = `
          <span>Remover todos</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M10.499 7.68726C10.8097 7.68726 11.0615 7.9391 11.0615 8.24976V12.7498C11.0615 13.0604 10.8097 13.3123 10.499 13.3123C10.1884 13.3123 9.93652 13.0604 9.93652 12.7498V8.24976C9.93652 7.9391 10.1884 7.68726 10.499 7.68726Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.50098 7.68726C7.81164 7.68726 8.06348 7.9391 8.06348 8.24976V12.7498C8.06348 13.0604 7.81164 13.3123 7.50098 13.3123C7.19032 13.3123 6.93848 13.0604 6.93848 12.7498V8.24976C6.93848 7.9391 7.19032 7.68726 7.50098 7.68726Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 4.68774C4.81066 4.68774 5.0625 4.93958 5.0625 5.25024V14.2502C5.0625 14.768 5.48223 15.1877 6 15.1877H12C12.5178 15.1877 12.9375 14.768 12.9375 14.2502V5.25024C12.9375 4.93958 13.1893 4.68774 13.5 4.68774C13.8107 4.68774 14.0625 4.93958 14.0625 5.25024V14.2502C14.0625 15.3893 13.1391 16.3127 12 16.3127H6C4.86091 16.3127 3.9375 15.3893 3.9375 14.2502V5.25024C3.9375 4.93958 4.18934 4.68774 4.5 4.68774Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M2.43848 5.25024C2.43848 4.93958 2.69032 4.68774 3.00098 4.68774H15.001C15.3116 4.68774 15.5635 4.93958 15.5635 5.25024C15.5635 5.5609 15.3116 5.81274 15.001 5.81274H3.00098C2.69032 5.81274 2.43848 5.5609 2.43848 5.25024Z" fill="#00a0b1"></path>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.24798 1.99844C6.34327 1.80788 6.53804 1.6875 6.7511 1.6875H11.2511C11.4642 1.6875 11.6589 1.80788 11.7542 1.99844L13.2542 4.99844C13.3931 5.27631 13.2805 5.61418 13.0027 5.75312C12.7248 5.89205 12.3869 5.77942 12.248 5.50156L10.9035 2.8125H7.09874L5.75422 5.50156C5.61528 5.77942 5.27741 5.89205 4.99954 5.75312C4.72168 5.61418 4.60905 5.27631 4.74798 4.99844L6.24798 1.99844Z" fill="#00a0b1"></path>
          </svg>
        `;
      }
    }
  };

  teardownRefazer() {
    // Método de limpeza para a classe CartDiscount
    // Por enquanto vazio, pode ser expandido conforme necessário
  }
}

customElements.define("cart-discount-component", CartDiscount);

class CartShipping extends HTMLElement {
  fmtBRL(v) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(v) || 0);
  }

  deliveryHtml(range) {
    const _isDateLike = (v) =>
      typeof v === "string" && !Number.isNaN(Date.parse(v));
    const _toDate = (v) => {
      if (v == null) return null;
      if (typeof v === "number" && Number.isFinite(v)) {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + v);
        return d;
      }
      if (_isDateLike(v)) return new Date(v);
      return null;
    };
    const _fmt = (d) =>
      d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    const _rel = (d) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const cmp = new Date(d);
      cmp.setHours(0, 0, 0, 0);
      const diff = Math.round((cmp - today) / 86400000);
      if (diff === 0) return "Hoje";
      if (diff === 1) return "Amanhã";
      if (diff > 1 && diff <= 7) {
        const wd = cmp.toLocaleDateString("pt-BR", { weekday: "short" });
        return `${wd} (${_fmt(cmp)})`;
      }
      return _fmt(cmp);
    };

    if (!range) return "";
    if (typeof range === "string") return range;

    let min = range.min ?? range.minimum ?? range.from ?? range[0] ?? null;
    let max = range.max ?? range.maximum ?? range.to ?? range[1] ?? null;
    const unit = String(range.unit || range.units || range.type || "");

    if (typeof min === "number" || typeof max === "number") {
      const same = (min ?? max) === (max ?? min);
      const qtd = same ? min ?? max : `${min ?? "?"}–${max ?? "?"}`;
      const uteis = unit.includes("business");
      return `em ${qtd} ${uteis ? "dias úteis" : "dias"}`;
    }

    const dMin = _toDate(min),
      dMax = _toDate(max);
    if (dMin && dMax) {
      if (dMin.toDateString() === dMax.toDateString()) return _rel(dMin);
      const diff = Math.max(0, Math.round((dMax - dMin) / 86400000));
      const a = _rel(dMin),
        b = diff <= 14 ? _rel(dMax) : _fmt(dMax);
      return `entre ${a} e ${b}`;
    }
    if (dMin) return `a partir de ${_rel(dMin)}`;
    if (dMax) return `até ${_rel(dMax)}`;
    return "";
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.open = this.querySelector(".insert-cep");
    this.error = this.querySelector(".shipping-component-error");

    if (this.form) this.form.addEventListener("submit", this.apply);
    if (this.open) this.open.addEventListener("click", this.toggle);

    const savedRates = localStorage.getItem("shippingRates");
    const selectedRate = localStorage.getItem("selectedShippingRate");

    if (savedRates && selectedRate) {
      this.renderSelected(JSON.parse(selectedRate));
    } else if (savedRates) {
      // Se há rates salvos mas não há seleção, selecionar automaticamente o mais barato
      const rates = JSON.parse(savedRates);
      rates.sort((a, b) => a.price - b.price);
      const cheapestRate = rates[0];
      localStorage.setItem(
        "selectedShippingRate",
        JSON.stringify(cheapestRate)
      );
      this.renderSelected(cheapestRate);
    }
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener("submit", this.apply);
    this.teardownRefazer();
  }

  toggle = (e) => {
    e.preventDefault();
    const isOpen = this.dataset.open === "opened";
    this.dataset.open = isOpen ? "closed" : "opened";

    if (isOpen) {
      const cepInput = this.form?.querySelector('[name="shipping-component"]');
      if (cepInput) cepInput.value = "";
      this.error.hidden = true;
    }
    this.open.textContent = isOpen ? "Calcule seu frete" : "Fechar";
  };

  apply = async (e) => {
    e.preventDefault();

    const raw =
      this.form.querySelector('[name="shipping-component"]')?.value || "";
    const zip = raw.replace(/\D/g, "");
    if (zip.length !== 8) {
      this.error.textContent = "Informe um CEP válido (8 dígitos).";
      this.error.hidden = false;
      return;
    }

    const spinner = this.form.querySelector(".form-loading");
    spinner.classList.remove("hidden");
    this.error.hidden = true;

    try {
      // Salvar o CEP para recálculos automáticos futuros
      localStorage.setItem("lastCalculatedCep", zip);

      // Executar cálculo usando o método refatorado
      await this.calculateShippingRates(zip);

      // Fechar o formulário após calcular
      this.dataset.open = "closed";
      this.open.textContent = "Calcule seu frete";

      // Recarregar página se estiver na URL /cart após selecionar frete
      if (window.location.pathname === "/cart") {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (err) {
      console.error(err);
      this.error.textContent =
        err.message || "Erro ao consultar frete. Tente novamente.";
      this.error.hidden = false;
    } finally {
      spinner.classList.add("hidden");
    }
  };

  // helper de polling (coloque dentro da mesma classe)
  pollRates = async (url, { tries = 12, delay = 500 } = {}) => {
    for (let i = 0; i < tries; i++) {
      const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data?.shipping_rates) && data.shipping_rates.length)
          return data.shipping_rates;
      }
      await new Promise((r) => setTimeout(r, delay));
    }
    return [];
  };

  renderList(rates) {
    // Método mantido para compatibilidade, mas agora seleciona automaticamente a opção mais barata
    rates.sort((a, b) => a.price - b.price);
    const cheapestRate = rates[0];
    localStorage.setItem("selectedShippingRate", JSON.stringify(cheapestRate));
    this.renderSelected(cheapestRate);

    // Recarregar página se estiver na URL /cart após selecionar frete
    if (window.location.pathname === "/cart") {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }

  renderSelected(selected) {
    const listBox = this.querySelector(".selected-shipping-components");
    const wrapper = listBox.querySelector(".shipping-wrapper");
    let resume = listBox.querySelector(".shipping-resume");
    if (!resume) {
      resume = document.createElement("div");
      resume.className = "shipping-resume";
      listBox.appendChild(resume);
    }

    wrapper.style.display = "none";
    listBox.classList.remove("hidden");
    this.classList.add("shipping-component-applied");

    const prazo = this.deliveryHtml(selected.delivery_range);
    resume.innerHTML = `
      <li class="shipping-component-item">
        <span class="shipping-selected-wrapper">
          <!-- ícone omitido -->
          <span>${selected.name} ${prazo ? `(até ${prazo})` : ""}</span>
        </span>
        <span>${
          Number(selected.price) === 0 ? "Grátis" : this.fmtBRL(selected.price)
        }</span>
        <button type="button" class="remove-shipping-component" aria-label="Refazer cálculo">&#10005;</button>
      </li>
    `;

    this.setAttribute("data-open", "closed");
    this.teardownRefazer?.();
    this._refazerBtn = resume.querySelector(".remove-shipping-component");
    this._refazerBtn?.addEventListener("click", this.clearShipping);
  }

  teardownRefazer() {
    if (this._refazerBtn) {
      this._refazerBtn.removeEventListener("click", this.clearShipping);
      this._refazerBtn = null;
    }
  }

  clearShipping = () => {
    localStorage.removeItem("shippingRates");
    localStorage.removeItem("selectedShippingRate");
    localStorage.removeItem("lastCalculatedCep"); // Limpar CEP salvo também

    const listBox = this.querySelector(".selected-shipping-components");
    const wrapper = listBox?.querySelector(".shipping-wrapper");
    const resume = listBox?.querySelector(".shipping-resume");

    if (resume) resume.remove(); // remove apenas o resumo
    if (wrapper) wrapper.style.display = ""; // volta a lista pro display padrão
    listBox?.classList.add("hidden");

    this.classList.remove("shipping-component-applied");
    this.dataset.open = "opened";
    this.open.textContent = "Fechar";

    const cepInput = this.form?.querySelector('[name="shipping-component"]');
    if (cepInput) {
      cepInput.value = "";
      cepInput.focus();
    }

    // Recarregar página se estiver na URL /cart
    if (window.location.pathname === "/cart") {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  };

  // Método para recalcular automaticamente o frete quando o carrinho muda
  autoRecalculateShipping = async () => {
    // this.log("🚀 Iniciando recálculo automático do frete...");

    // Verificar se há um CEP salvo anteriormente
    const savedCep = localStorage.getItem("lastCalculatedCep");
    if (!savedCep || savedCep.length !== 8) {
    //   this.log("❌ CEP não encontrado ou inválido:", savedCep);
      return; // Não há CEP válido salvo
    }

    // Só recalcular se não estivermos na página do carrinho (para evitar reload infinito)
    if (window.location.pathname === "/cart") {
    //   this.log("❌ Evitando recálculo na página /cart");
      return;
    }

    // this.log("✅ CEP válido encontrado:", savedCep);

    try {
      // Mostrar skeleton loading
    //   this.log("🎨 Mostrando skeleton loading...");
      this.showRecalculatingState();

      // Adicionar delay para garantir que a animação seja visível
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Simular o preenchimento do formulário e cálculo
      const cepInput = this.form?.querySelector('[name="shipping-component"]');
      if (cepInput) {
        cepInput.value = savedCep;
      }

      // Executar o cálculo automaticamente
    //   this.log("🔄 Calculando frete...");
      await this.calculateShippingRates(savedCep);

      // Adicionar delay antes de esconder para ver o resultado
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Esconder skeleton loading
    //   this.log("✅ Escondendo skeleton loading...");
      this.hideRecalculatingState();
    } catch (error) {
    //   this.log("❌ Erro ao recalcular frete automaticamente:", error);
      // Esconder skeleton loading em caso de erro
      this.hideRecalculatingState();
    }
  };

  // Métodos para controlar a animação de recálculo
  showRecalculatingState = () => {
    // this.log("🎨 showRecalculatingState chamado");
    const listBox = this.querySelector(".selected-shipping-components");
    const resume = listBox?.querySelector(".shipping-resume");

    // this.log("📦 listBox:", listBox);
    // this.log("📄 resume:", resume);

    if (resume) {
    //   this.log("✅ Adicionando classe shipping-recalculating");
      this.classList.add("shipping-recalculating");
    } else {
    //   this.log("🔧 Criando skeleton temporário");
      // Se não há frete selecionado, criar skeleton temporário
      this.createTemporarySkeleton();
    }
  };

  hideRecalculatingState = () => {
    // this.log("🎨 hideRecalculatingState chamado");
    this.classList.remove("shipping-recalculating");
    this.removeTemporarySkeleton();
  };

  createTemporarySkeleton = () => {
    // this.log("🔧 createTemporarySkeleton chamado");
    const listBox = this.querySelector(".selected-shipping-components");
    if (!listBox || listBox.querySelector(".shipping-skeleton-temp")) {
    //   this.log("❌ listBox não encontrado ou skeleton já existe");
      return;
    }

    const skeletonHtml = `
      <div class="shipping-skeleton-temp" style="padding: 10px 0;">
        <div class="shipping-skeleton wide"></div>
        <div class="shipping-skeleton medium"></div>
        <div class="shipping-skeleton narrow"></div>
      </div>
    `;

    // this.log("✅ Inserindo skeleton HTML");
    listBox.insertAdjacentHTML("beforeend", skeletonHtml);
    listBox.classList.remove("hidden");
  };

  removeTemporarySkeleton = () => {
    // this.log("🗑️ removeTemporarySkeleton chamado");
    const skeleton = this.querySelector(".shipping-skeleton-temp");
    if (skeleton) {
    //   this.log("✅ Removendo skeleton");
      skeleton.remove();
    } else {
    //   this.log("❌ Skeleton não encontrado para remover");
    }
  };

  calculateShippingRates = async (zip) => {
    try {
      // 1) PREPARE
      const prepare = await fetch("/cart/prepare_shipping_rates.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          shipping_address: {
            zip,
            country: "Brazil",
            province: "MG",
          },
        }),
      });

      if (!prepare.ok) {
        const err = await prepare.json().catch(() => ({}));
        throw new Error(
          err?.errors || "Não foi possível iniciar o cálculo de frete."
        );
      }

      // 2) POLLING (GET) até ter tarifas
      const params = new URLSearchParams({
        "shipping_address[zip]": zip,
        "shipping_address[country]": "Brazil",
        "shipping_address[province]": "MG",
      });
      const rates = await this.pollRates(`/cart/shipping_rates.json?${params}`);

      if (!rates.length) {
        // Se não há rates disponíveis, limpar seleção anterior
        localStorage.removeItem("shippingRates");
        localStorage.removeItem("selectedShippingRate");
        return;
      }

      rates.sort((a, b) => a.price - b.price);
      localStorage.setItem("shippingRates", JSON.stringify(rates));

      // Selecionar automaticamente a opção mais barata (primeira após ordenação)
      const cheapestRate = rates[0];
      localStorage.setItem(
        "selectedShippingRate",
        JSON.stringify(cheapestRate)
      );
      this.renderSelected(cheapestRate);
    } catch (error) {
    //   this.log("Erro ao calcular frete:", error);
    }
  };
}

customElements.define("cart-shipping-component", CartShipping);
